import { createContext, use, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { useCopilotSession, CopilotProvider, useCopilotInstruction } from '@customerio/hydra-copilot/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';

import { useCopilotSessionAutoName } from '../hooks/use-copilot-session-auto-name';
import { useCopilotSessionUrlSync } from '../hooks/use-copilot-session-url-sync';
import { useMasqAccessLevel } from '../hooks/use-masq-access-level';
import { useTrackCopilotWorkspace } from '../workspace-context';
import {
  Box,
  Code,
  CodeBlock,
  Divider,
  Emphasis,
  Heading,
  Image,
  Link,
  List,
  ListItem,
  Paragraph,
  Strong,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
} from '@customerio/pluma-components/react';
import { bundledLanguages } from 'shiki';
import type { MarkdownComponents } from '@customerio/hydra-copilot/react';
import { open as openSidebar } from '@/features/sidebar';
import { Lightbox, useLightbox } from '@/components/overlays/lightbox';
import { copilotLibrary, copilotPromptOptions } from './openui-library';
import { isInternalLink, isSafeUrl, isSameWorkspaceLink } from '@/utils/url';
import * as markdownStyles from './markdown.css';
import { MarkdownTable } from './primitives/markdown-table';
import { CopilotFileLink } from './copilot-file-link';
import { CopilotMermaidDiagram } from './primitives/mermaid-diagram';

type CopilotSession = ReturnType<typeof useCopilotSession>;

interface CopilotChatInputValue {
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
}

// Split so session-only consumers don't re-render on every keystroke.
const CopilotSessionContext = createContext<CopilotSession | null>(null);
const CopilotChatInputContext = createContext<CopilotChatInputValue | null>(null);

export function useCopilotSessionContext() {
  const session = use(CopilotSessionContext);
  if (session == null) {
    throw new Error('useCopilotSessionContext must be used within CopilotSessionProvider');
  }
  return session;
}

export function useCopilotChatContext() {
  const session = use(CopilotSessionContext);
  const input = use(CopilotChatInputContext);
  if (session == null || input == null) {
    throw new Error('useCopilotChatContext must be used within CopilotSessionProvider');
  }
  return { session, ...input };
}

type MarkdownLinkProps = { href?: string; children: React.ReactNode };

function MarkdownLink({ href, children }: MarkdownLinkProps) {
  if (href != null) {
    if (href.startsWith('copilot-file://')) {
      const filePath = href.slice('copilot-file://'.length);
      const displayName = typeof children === 'string' ? children : (filePath.split('/').pop() ?? filePath);
      return <CopilotFileLink path={filePath} displayName={displayName} />;
    }

    const internal = isInternalLink(href);
    if (internal != null && isSameWorkspaceLink(href)) {
      return (
        <Link
          href={internal.relativePath}
          variant="primary"
          onClick={(event_) => {
            if (!event_.metaKey && !event_.ctrlKey) openSidebar('copilot');
          }}
        >
          {children}
        </Link>
      );
    }
  }
  const safeHref = href != null && isSafeUrl(href) ? href : undefined;
  return (
    <Link href={safeHref} isExternal={true} variant="secondary" icon="exit" iconPosition="trailing">
      {children}
    </Link>
  );
}

type MarkdownImageProps = { src?: string; alt?: string };

function MarkdownImage({ src, alt }: MarkdownImageProps) {
  const lightbox = useLightbox();
  return (
    <>
      <Image
        src={src}
        alt={alt}
        className={markdownStyles.markdownImage}
        onClick={() => {
          if (src != null) lightbox.open(src);
        }}
      />
      <Lightbox src={lightbox.src} onClose={lightbox.close} />
    </>
  );
}

const markdownComponents: MarkdownComponents = {
  // Render as div: markdown paragraphs can wrap block-level content that is invalid inside a <p>.
  p: ({ children }) => (
    <Paragraph as="div" className={markdownStyles.paragraph} color="inherit">
      {children}
    </Paragraph>
  ),
  h1: ({ children }) => (
    <Heading level="2" color="inherit">
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading level="3" color="inherit">
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading level="4" color="inherit">
      {children}
    </Heading>
  ),
  h4: ({ children }) => (
    <Heading level="5" color="inherit">
      {children}
    </Heading>
  ),
  h5: ({ children }) => (
    <Heading level="6" color="inherit">
      {children}
    </Heading>
  ),
  h6: ({ children }) => (
    <Heading level="6" color="inherit">
      {children}
    </Heading>
  ),
  strong: ({ children }) => <Strong>{children}</Strong>,
  em: ({ children }) => <Emphasis>{children}</Emphasis>,
  code: ({ className, children }) => {
    const language = typeof className === 'string' ? className.replace('language-', '') : '';
    const isMultiline = typeof children === 'string' && children.includes('\n');
    const isBlock = language.length > 0 || isMultiline;

    // During streaming, partial language identifiers (e.g. "open" from "openui") can arrive.
    // Fall back to 'plain' if the language isn't recognized by Shiki to avoid render errors.
    const isKnownLanguage = language.length > 0 && language in bundledLanguages;
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const resolvedLanguage = (isKnownLanguage ? language : 'plain') as Parameters<typeof CodeBlock>[0]['language'];

    if (isBlock) {
      const code = typeof children === 'string' ? children.replace(/\n$/, '') : String(children);
      return (
        <CodeBlock code={code} language={resolvedLanguage} withCopyButton={true} withLanguageLabel={isKnownLanguage} />
      );
    }

    return <Code>{children}</Code>;
  },
  blockquote: ({ children }) => (
    <Box
      pl="200"
      as="blockquote"
      ml="0"
      mr="0"
      style={{
        borderLeft: `${themeVars.border['width-050']} solid ${themeVars.color['border-minimal']}`,
      }}
    >
      {children}
    </Box>
  ),
  ul: ({ children }) => <List type="unordered">{children}</List>,
  ol: ({ children }) => <List type="ordered">{children}</List>,
  li: ({ children }) => <ListItem>{children}</ListItem>,
  a: ({ href, children }) => <MarkdownLink href={href}>{children}</MarkdownLink>,
  img: ({ src, alt }) => <MarkdownImage src={src} alt={alt} />,
  hr: () => <Divider my="100" />,
  table: ({ children }) => <MarkdownTable>{children}</MarkdownTable>,
  thead: ({ children }) => <TableThead>{children}</TableThead>,
  tbody: ({ children }) => <TableTbody>{children}</TableTbody>,
  tr: ({ children }) => <TableTr>{children}</TableTr>,
  th: ({ children }) => <TableTh>{children}</TableTh>,
  td: ({ children }) => <TableTd>{children}</TableTd>,
};

export { useMasqAccessLevel } from '../hooks/use-masq-access-level';

type CopilotSessionProviderProps = { children: ReactNode };

export function CopilotSessionProvider({ children }: CopilotSessionProviderProps) {
  const session = useCopilotSession({ isPrimary: true });
  const [input, setInput] = useState('');
  const { isMasq, isMasqWithoutAccess } = useMasqAccessLevel();

  useCopilotSessionAutoName(session);
  useCopilotSessionUrlSync(session, setInput);
  useTrackCopilotWorkspace();

  useCopilotInstruction({
    instruction: `The current user is masquerading into this account but does NOT have masquerade access permissions.
You will NOT be able to read or modify any account data — all API calls and tool invocations that access this account's data will fail with a 403 forbidden error.
You can still answer general questions about Customer.io, help with strategy, explain concepts, or assist with tasks that don't require accessing this specific account's data.
If the user asks you to do something that requires account data access, explain that their masquerade session does not have sufficient permissions and suggest they request masquerade access from an admin.`,
    isEnabled: isMasqWithoutAccess,
  });

  useCopilotInstruction({
    instruction: `The current user is masquerading into this account with read-only access.
You can read account data but you cannot modify it — any API calls or tool invocations that attempt to write, update, or delete account data will fail with a 403 forbidden error.
If the user asks you to make changes, explain that their masquerade session has read-only access and suggest they log in directly as an admin if changes are needed.`,
    isEnabled: isMasq && !isMasqWithoutAccess,
  });

  const inputValue: CopilotChatInputValue = useMemo(() => ({ input, setInput }), [input, setInput]);

  return (
    <CopilotSessionContext.Provider value={session}>
      <CopilotChatInputContext.Provider value={inputValue}>
        <CopilotProvider
          markdown={markdownComponents}
          library={copilotLibrary}
          libraryPromptOptions={copilotPromptOptions}
          mermaidComponent={CopilotMermaidDiagram}
        >
          {children}
        </CopilotProvider>
      </CopilotChatInputContext.Provider>
    </CopilotSessionContext.Provider>
  );
}
